import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Send, Save, CheckCircle2, UserMinus, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const PKG_STATUS_LABEL = { pending: "Requested — pending", accepted: "Accepted", rejected: "Rejected", making: "Making", delivery: "Out for delivery", delivered: "Delivered" };

export default function ReportEditor({ report, onChange, onConfirmed }) {
  const nav = useNavigate();
  const [title, setTitle] = useState(report.title);
  const [body, setBody] = useState(report.body);
  const [email, setEmail] = useState(report.client_email || "");
  const [busy, setBusy] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pkgRequest, setPkgRequest] = useState(undefined); // undefined = loading, null = none yet
  const dirty = title !== report.title || body !== report.body || email !== (report.client_email || "");
  const s = report.sizing;
  const cur = s.economics.currency;

  useEffect(() => {
    let alive = true;
    api.get("/package-requests").then((r) => {
      if (alive) setPkgRequest(r.data.find((p) => p.report_id === report.id) || null);
    }).catch(() => alive && setPkgRequest(null));
    return () => { alive = false; };
  }, [report.id]);

  const requestPackage = async () => {
    setBusy("package");
    try {
      const { data } = await api.post(`/reports/${report.id}/request-package`);
      setPkgRequest(data);
      toast.success("Package requested", { description: "Sent to the super admin for approval." });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try { const { data } = await api.put(`/reports/${report.id}`, { title, body, client_email: email }); onChange(data); toast.success("Draft saved"); return data; }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const download = async () => {
    setBusy("pdf");
    try {
      if (dirty) await api.put(`/reports/${report.id}`, { title, body, client_email: email }).then((r) => onChange(r.data));
      const res = await api.get(`/reports/${report.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `KyraLink_${report.serial}_solar_report.pdf` });
      a.click(); URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const send = async () => {
    if (!email) return toast.error("Add the client's email first");
    setBusy("send");
    try {
      if (dirty) await api.put(`/reports/${report.id}`, { title, body, client_email: email });
      const { data } = await api.post(`/reports/${report.id}/send`, { to_email: email });
      onChange(data); toast.success(`Proposal sent to ${email}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const confirm = async () => {
    setConfirmOpen(false);
    setBusy("confirm");
    try {
      await api.post(`/reports/${report.id}/confirm`);
      toast.success("Proposal confirmed and archived", { description: `${report.serial} is freed and ready for a new client.` });
      onConfirmed?.();
      nav("/history");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  return (
    <div className="max-w-5xl mx-auto p-8 animate-rise" data-testid="report-editor">
      <div className="flex items-start justify-between gap-6 pb-6 border-b border-kl-lineLight">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <Link to={`/devices/${report.device_id}`} data-testid="report-back-to-device" title="Back to this client's dashboard" className="mt-1 p-2 border border-kl-lineLight hover:bg-kl-black hover:text-white transition-colors duration-150 shrink-0">
            <ArrowLeft size={16} />
          </Link>
          <div className="min-w-0 flex-1">
            <p className="kl-label">{report.serial} · {report.client_name} · {report.region ? `${report.region} · ${report.sizing.economics.currency} · ` : ""}{report.status === "sent" ? `sent ${fmt.time(report.sent_at)}` : "draft"}</p>
            <input data-testid="report-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full truncate font-display font-light text-3xl mt-1 bg-transparent border-0 border-b border-transparent focus:border-kl-black focus:outline-none transition-colors" />
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={save} disabled={!dirty || !!busy} data-testid="report-save-button" className="kl-btn-outline"><Save size={15} /> {busy === "save" ? "Saving…" : "Save"}</button>
          <button onClick={download} disabled={!!busy} data-testid="report-download-button" className="kl-btn-dark"><Download size={15} /> {busy === "pdf" ? "Building…" : "Download PDF"}</button>
          <button onClick={send} disabled={!!busy} data-testid="report-send-button" className="kl-btn-primary"><Send size={15} /> {busy === "send" ? "Sending…" : "Send to client"}</button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6 mt-6">
        <div className="col-span-12 lg:col-span-8">
          <label className="block mb-5">
            <span className="kl-label">Recipient</span>
            <input data-testid="report-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@example.com" className="kl-input" />
          </label>
          <span className="kl-label">Engineer's note · editable</span>
          <textarea data-testid="report-body-textarea" value={body} onChange={(e) => setBody(e.target.value)} rows={22} className="mt-1 w-full border border-kl-lineLight p-5 font-body text-[14px] leading-relaxed focus:outline-none focus:border-kl-black transition-colors resize-y" />
        </div>
        <div className="col-span-12 lg:col-span-4 space-y-4">
          <div className="kl-panel-dark p-5" data-testid="report-sizing-summary">
            <p className="kl-label-dark">Included specification</p>
            <div className="mt-3 space-y-2 font-mono text-sm">
              <div className="flex justify-between"><span className="text-kl-muted">Panels</span><span>{s.pv.count} × {s.pv.panel?.rating} W</span></div>
              <div className="flex justify-between"><span className="text-kl-muted">Array</span><span>{s.pv.array_kw} kWp</span></div>
              <div className="flex justify-between"><span className="text-kl-muted">Inverter</span><span>{s.inverter.count} × {s.inverter.recommended?.rating} kW</span></div>
              <div className="flex justify-between"><span className="text-kl-muted">Battery</span><span>{s.battery.total_kwh} kWh</span></div>
              <div className="flex justify-between border-t border-kl-line pt-2 mt-2"><span className="text-kl-muted">Cost</span><span className="text-kl-green">{fmt.money(s.economics.system_cost, cur)}</span></div>
              {s.economics.secondary && <div className="flex justify-between text-xs"><span className="text-kl-muted">≈ {s.economics.secondary.currency}</span><span data-testid="report-secondary-cost">{fmt.money(s.economics.secondary.system_cost, s.economics.secondary.currency)}</span></div>}
              <div className="flex justify-between"><span className="text-kl-muted">Payback</span><span>{s.economics.payback_years} yrs</span></div>
            </div>
            <div className="mt-4 grid gap-0.5" style={{ gridTemplateColumns: "repeat(8, 1fr)" }}>
              {Array.from({ length: s.pv.count }).map((_, i) => <div key={i} className="aspect-[1/2] bg-kl-surface border border-kl-green/60" />)}
            </div>
          </div>
          {pkgRequest ? (
            <div className="kl-panel p-4 flex items-center gap-2" data-testid="report-package-status">
              <PackageCheck size={15} className="text-kl-green shrink-0" />
              <span className="text-sm">Package: <span className="font-display">{PKG_STATUS_LABEL[pkgRequest.status] || pkgRequest.status}</span></span>
            </div>
          ) : pkgRequest === null ? (
            <button onClick={requestPackage} disabled={!!busy} data-testid="report-request-package-button" className="kl-btn-outline w-full h-11">
              <PackageCheck size={15} /> {busy === "package" ? "Requesting…" : "Request package"}
            </button>
          ) : null}
          <div className="kl-panel p-5" data-testid="report-send-log">
            <p className="kl-label">Delivery log</p>
            {report.sent_log?.length ? (
              <ul className="mt-3 space-y-2">
                {report.sent_log.slice().reverse().map((e, i) => (
                  <li key={i} className="flex gap-2 text-xs"><CheckCircle2 size={14} className="text-kl-greenDark shrink-0 mt-0.5" /><span><span className="font-mono">{e.to}</span><br /><span className="text-kl-mutedLight">{fmt.time(e.at)} · by {e.by} · {e.channel}</span></span></li>
                ))}
              </ul>
            ) : <p className="text-xs text-kl-mutedLight mt-2">Not sent yet. Sending is simulated for now and recorded here.</p>}
          </div>
          <button onClick={() => setConfirmOpen(true)} disabled={!!busy} data-testid="report-confirm-button" className="kl-btn-outline w-full h-11">
            <UserMinus size={15} /> {busy === "confirm" ? "Unassigning…" : "Unassign device"}
          </button>
          <p className="text-[11px] text-kl-mutedLight leading-relaxed">Frees {report.serial} from {report.client_name} — archives this proposal into History first, then clears the site so the meter can be registered to a different client.</p>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Unassign ${report.serial} from ${report.client_name}?`}
        description={
          <>
            This will:<br />
            • Archive {report.client_name}'s readings, sizing and this proposal into History (still viewable and downloadable there)<br />
            • Clear {report.client_name}'s name and site address from {report.serial}<br />
            • Reset the physical meter itself (if paired) — it clears its customer, profiles and session and goes back to a fresh, unassigned state<br />
            • Free {report.serial} so it can be registered to a different client<br />
            <br />
            This can't be undone.
          </>
        }
        confirmLabel="Unassign & archive"
        danger={false}
        onConfirm={confirm}
      />
    </div>
  );
}
